import { supabase } from '../lib/supabase'
import type { Profile, Match, Like, Message, Conversation } from '../lib/supabase'

export interface UserStats {
  likes_sent: number;
  likes_received: number;
  super_likes_sent: number;
  super_likes_received: number;
  matches: number;
  conversations: number;
  messages_sent: number;
  messages_received: number;
  profile_views: number;
}

export interface PotentialMatch extends Profile {
  distance_km?: number;
  compatibility_score?: number;
}

export class SupabaseService {
  // ============================================================================
  // PROFILE MANAGEMENT
  // ============================================================================

  async createProfile(profileData: Partial<Profile>): Promise<Profile | null> {
    try {
      console.log('Creating profile with data:', profileData);
      
      const { data, error } = await supabase
        .from('profiles')
        .insert([{
          user_id: profileData.user_id,
          pi_username: profileData.pi_username || '',
          first_name: profileData.first_name || '',
          last_name: profileData.last_name || '',
          email: profileData.email || '',
          phone: profileData.phone || '',
          age: profileData.age || 25,
          gender: profileData.gender || 'other',
          bio: profileData.bio || '',
          occupation: profileData.occupation || '',
          education: profileData.education || '',
          interests: profileData.interests || [],
          languages: profileData.languages || [],
          photos: profileData.photos || [],
          location_city: profileData.location_city || '',
          location_country: profileData.location_country || '',
          latitude: profileData.latitude || 0,
          longitude: profileData.longitude || 0,
          looking_for: profileData.looking_for || [],
          relationship_type: profileData.relationship_type || 'casual',
          min_age: profileData.min_age || 18,
          max_age: profileData.max_age || 50,
          max_distance: profileData.max_distance || 50,
          is_active: profileData.is_active !== undefined ? profileData.is_active : true,
          is_premium: profileData.is_premium || false,
          is_verified: profileData.is_verified || false,
          is_pi_pioneer: profileData.is_pi_pioneer || false
        }])
        .select()
        .single()

      if (error) {
        console.error('Error creating profile:', error);
        throw error;
      }
      
      console.log('Profile created successfully:', data);
      return data;
    } catch (error) {
      console.error('Error in createProfile:', error);
      throw error;
    }
  }

  async getProfile(userId: string): Promise<Profile | null> {
    try {
      console.log('Getting profile for user:', userId);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error getting profile:', error);
        return null;
      }
      
      console.log('Profile retrieved:', data);
      return data;
    } catch (error) {
      console.error('Error in getProfile:', error);
      return null;
    }
  }

  async updateProfile(userId: string, updates: Partial<Profile>): Promise<Profile | null> {
    try {
      console.log('Updating profile for user:', userId, updates);
      
      const { data, error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select()
        .single()

      if (error) {
        console.error('Error updating profile:', error);
        throw error;
      }
      
      console.log('Profile updated successfully:', data);
      return data;
    } catch (error) {
      console.error('Error in updateProfile:', error);
      return null;
    }
  }

  async deleteProfile(userId: string): Promise<boolean> {
    try {
      console.log('Deleting profile for user:', userId);
      
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('user_id', userId)

      if (error) {
        console.error('Error deleting profile:', error);
        return false;
      }
      
      console.log('Profile deleted successfully');
      return true;
    } catch (error) {
      console.error('Error in deleteProfile:', error);
      return false;
    }
  }

  // ============================================================================
  // USER SETTINGS
  // ============================================================================

  async createUserSettings(userId: string): Promise<boolean> {
    try {
      console.log('Creating user settings for:', userId);
      
      const { error } = await supabase
        .from('user_settings')
        .insert([{
          user_id: userId,
          push_notifications: true,
          email_notifications: true,
          new_matches: true,
          new_messages: true,
          super_likes: true,
          profile_views: false,
          promotions: true,
          show_online_status: true,
          show_read_receipts: true,
          theme: 'light',
          language: 'en'
        }]);

      if (error) {
        console.error('Error creating user settings:', error);
        return false;
      }
      
      console.log('User settings created successfully');
      return true;
    } catch (error) {
      console.error('Error in createUserSettings:', error);
      return false;
    }
  }

  // ============================================================================
  // MATCHING SYSTEM
  // ============================================================================

  async getPotentialMatches(userId: string, limit: number = 10): Promise<PotentialMatch[]> {
    try {
      console.log('Getting potential matches for user:', userId);
      
      // Get current user's profile and preferences
      const { data: currentProfile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (profileError || !currentProfile) {
        console.error('Error getting current profile:', profileError);
        return []
      }

      // Get users that have been liked by current user
      const { data: likedUsers, error: likedError } = await supabase
        .from('likes')
        .select('to_user_id')
        .eq('from_user_id', userId)

      if (likedError) {
        console.error('Error getting liked users:', likedError);
      }

      const likedUserIds = likedUsers?.map(like => like.to_user_id) || []

      // Get potential matches
      let query = supabase
        .from('profiles')
        .select('*')
        .neq('user_id', userId)
        .eq('is_active', true)
        .gte('age', currentProfile.min_age)
        .lte('age', currentProfile.max_age)
        .limit(limit)

      // Only add the NOT IN filter if there are liked users
      if (likedUserIds.length > 0) {
        query = query.not('user_id', 'in', `(${likedUserIds.map(id => `"${id}"`).join(',')})`)
      }

      const { data: matches, error: matchError } = await query

      if (matchError) {
        console.error('Error getting potential matches:', matchError);
        return [];
      }
      
      console.log('Potential matches found:', matches?.length || 0);
      return matches || []
    } catch (error) {
      console.error('Error in getPotentialMatches:', error)
      return []
    }
  }

  async likeUser(fromUserId: string, toUserId: string, isSuper: boolean = false, message: string = ''): Promise<{ liked: boolean; matched: boolean; matchId?: string }> {
    try {
      console.log('Liking user:', { fromUserId, toUserId, isSuper });
      
      // Create like
      const { error: likeError } = await supabase
        .from('likes')
        .insert([{
          from_user_id: fromUserId,
          to_user_id: toUserId,
          is_super: isSuper,
          message: message
        }])

      if (likeError) {
        console.error('Error creating like:', likeError);
        return { liked: false, matched: false };
      }

      // Check if it's a mutual like (match will be created automatically by trigger)
      const { data: mutualLike } = await supabase
        .from('likes')
        .select('*')
        .eq('from_user_id', toUserId)
        .eq('to_user_id', fromUserId)
        .maybeSingle()

      if (mutualLike) {
        console.log('Mutual like detected, match should be created by trigger');
        
        // Get the match that was created by the trigger
        const { data: match } = await supabase
          .from('matches')
          .select('*')
          .or(`and(user1_id.eq.${fromUserId},user2_id.eq.${toUserId}),and(user1_id.eq.${toUserId},user2_id.eq.${fromUserId})`)
          .maybeSingle()

        return { liked: true, matched: true, matchId: match?.id }
      }

      console.log('Like created successfully, no mutual like yet');
      return { liked: true, matched: false }
    } catch (error) {
      console.error('Error in likeUser:', error)
      return { liked: false, matched: false }
    }
  }

  async getUserMatches(userId: string): Promise<(Match & { profile: Profile; lastMessage?: Message })[]> {
    try {
      console.log('Getting matches for user:', userId);
      
      const { data, error } = await supabase
        .from('matches')
        .select(`
          *,
          profile1:profiles!matches_user1_id_fkey(*),
          profile2:profiles!matches_user2_id_fkey(*)
        `)
        .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error getting matches:', error);
        return [];
      }

      const matches = (data || [])
        .filter(match => match.profile1 && match.profile2) // Filter out matches with missing profiles
        .map(match => {
          const otherProfile = match.user1_id === userId ? match.profile2 : match.profile1
          return {
            ...match,
            profile: otherProfile
          }
        })

      console.log('Matches found:', matches.length);
      return matches;
    } catch (error) {
      console.error('Error in getUserMatches:', error)
      return []
    }
  }

  // ============================================================================
  // MESSAGING SYSTEM
  // ============================================================================

  async sendMessage(senderId: string, receiverId: string, content: string, messageType: string = 'text'): Promise<Message | null> {
    try {
      console.log('Sending message:', { senderId, receiverId, content });
      
      // Get or create conversation
      let { data: conversation } = await supabase
        .from('conversations')
        .select('*')
        .or(`and(user1_id.eq.${senderId},user2_id.eq.${receiverId}),and(user1_id.eq.${receiverId},user2_id.eq.${senderId})`)
        .maybeSingle()

      if (!conversation) {
        console.log('Creating new conversation');
        const { data: newConversation, error: convError } = await supabase
          .from('conversations')
          .insert([{
            user1_id: senderId < receiverId ? senderId : receiverId,
            user2_id: senderId < receiverId ? receiverId : senderId,
            is_active: true
          }])
          .select()
          .single()

        if (convError) {
          console.error('Error creating conversation:', convError);
          throw convError;
        }
        conversation = newConversation
      }

      // Send message
      const { data, error } = await supabase
        .from('messages')
        .insert([{
          conversation_id: conversation.id,
          sender_id: senderId,
          receiver_id: receiverId,
          content: content,
          message_type: messageType,
          is_read: false
        }])
        .select()
        .single()

      if (error) {
        console.error('Error sending message:', error);
        throw error;
      }
      
      console.log('Message sent successfully');
      return data;
    } catch (error) {
      console.error('Error in sendMessage:', error)
      return null
    }
  }

  async getUserConversations(userId: string): Promise<(Conversation & { otherUser: Profile; lastMessage?: Message; unreadCount: number })[]> {
    try {
      console.log('Getting conversations for user:', userId);
      
      const { data, error } = await supabase
        .from('conversations')
        .select(`
          *,
          user1:profiles!conversations_user1_id_fkey(*),
          user2:profiles!conversations_user2_id_fkey(*)
        `)
        .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
        .eq('is_active', true)
        .order('last_message_at', { ascending: false })

      if (error) {
        console.error('Error getting conversations:', error);
        return [];
      }

      const conversations = (data || [])
        .filter(conv => conv.user1 && conv.user2) // Filter out conversations with missing profiles
        .map(conv => {
          const otherUser = conv.user1_id === userId ? conv.user2 : conv.user1
          return {
            ...conv,
            otherUser,
            unreadCount: 0 // TODO: Calculate actual unread count
          }
        })

      console.log('Conversations found:', conversations.length);
      return conversations;
    } catch (error) {
      console.error('Error in getUserConversations:', error)
      return []
    }
  }

  // ============================================================================
  // BLOCKING & REPORTING
  // ============================================================================

  async blockUser(blockerId: string, blockedId: string, reason: string = ''): Promise<boolean> {
    try {
      console.log('Blocking user:', { blockerId, blockedId, reason });
      
      const { error } = await supabase
        .from('blocks')
        .insert([{
          blocker_id: blockerId,
          blocked_id: blockedId,
          reason: reason
        }])

      if (error) {
        console.error('Error blocking user:', error);
        return false;
      }
      
      console.log('User blocked successfully');
      return true;
    } catch (error) {
      console.error('Error in blockUser:', error)
      return false
    }
  }

  async reportUser(reporterId: string, reportedId: string, reason: string, description: string = ''): Promise<boolean> {
    try {
      console.log('Reporting user:', { reporterId, reportedId, reason });
      
      const { error } = await supabase
        .from('reports')
        .insert([{
          reporter_id: reporterId,
          reported_id: reportedId,
          reason: reason,
          description: description
        }])

      if (error) {
        console.error('Error reporting user:', error);
        return false;
      }
      
      console.log('User reported successfully');
      return true;
    } catch (error) {
      console.error('Error in reportUser:', error)
      return false
    }
  }

  // ============================================================================
  // ANALYTICS & STATISTICS
  // ============================================================================

  async getUserStats(userId: string): Promise<UserStats> {
    try {
      console.log('Getting stats for user:', userId);
      
      // Use the database function if available, otherwise calculate manually
      const { data, error } = await supabase
        .rpc('get_user_stats', { user_uuid: userId })

      if (error) {
        console.log('RPC function not available, calculating stats manually');
        
        // Fallback to manual calculation
        const [
          likesSent,
          likesReceived,
          superLikesSent,
          superLikesReceived,
          matches,
          conversations,
          messagesSent,
          messagesReceived,
          profileViews
        ] = await Promise.all([
          supabase.from('likes').select('id', { count: 'exact' }).eq('from_user_id', userId),
          supabase.from('likes').select('id', { count: 'exact' }).eq('to_user_id', userId),
          supabase.from('likes').select('id', { count: 'exact' }).eq('from_user_id', userId).eq('is_super', true),
          supabase.from('likes').select('id', { count: 'exact' }).eq('to_user_id', userId).eq('is_super', true),
          supabase.from('matches').select('id', { count: 'exact' }).or(`user1_id.eq.${userId},user2_id.eq.${userId}`),
          supabase.from('conversations').select('id', { count: 'exact' }).or(`user1_id.eq.${userId},user2_id.eq.${userId}`),
          supabase.from('messages').select('id', { count: 'exact' }).eq('sender_id', userId),
          supabase.from('messages').select('id', { count: 'exact' }).eq('receiver_id', userId),
          supabase.from('profile_views').select('id', { count: 'exact' }).eq('viewed_id', userId)
        ]);

        return {
          likes_sent: likesSent.count || 0,
          likes_received: likesReceived.count || 0,
          super_likes_sent: superLikesSent.count || 0,
          super_likes_received: superLikesReceived.count || 0,
          matches: matches.count || 0,
          conversations: conversations.count || 0,
          messages_sent: messagesSent.count || 0,
          messages_received: messagesReceived.count || 0,
          profile_views: profileViews.count || 0
        };
      }
      
      console.log('Stats retrieved:', data);
      return data || {
        likes_sent: 0,
        likes_received: 0,
        super_likes_sent: 0,
        super_likes_received: 0,
        matches: 0,
        conversations: 0,
        messages_sent: 0,
        messages_received: 0,
        profile_views: 0
      }
    } catch (error) {
      console.error('Error in getUserStats:', error)
      return {
        likes_sent: 0,
        likes_received: 0,
        super_likes_sent: 0,
        super_likes_received: 0,
        matches: 0,
        conversations: 0,
        messages_sent: 0,
        messages_received: 0,
        profile_views: 0
      }
    }
  }

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  async checkDatabaseConnection(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .limit(1);

      if (error) {
        console.error('Database connection check failed:', error);
        return false;
      }
      
      console.log('Database connection successful');
      return true;
    } catch (error) {
      console.error('Database connection error:', error);
      return false;
    }
  }
}

export const supabaseService = new SupabaseService()